/** Canonical statuses for financial-dashboard claims workflow. */

import { isClaimEligibleForPayment } from "./claimPayableAmount";

export const CLAIM_STATUS = {
  /** Created + OCR run; submitter must confirm or cancel before queueing the claim thus it will be in draft status. */
  DRAFT: "DRAFT",

  /** This is the queue that the claim will be in after the submitter has confirmed the claim. */
  PENDING: "PENDING",
  
  /** This is the queue that the claim will be in after the reviewer has reviewed the claim. */
  REVIEW: "REVIEW",
  
  /** This is the queue that the claim will be in after the CFO has reviewed the claim. */
  PENDING_CFO: "PENDING_CFO",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  AMENDMENT_REQUESTED: "AMENDMENT_REQUESTED",
  PARTIAL_PAYMENT: "PARTIAL_PAYMENT",
  PAID: "PAID",
  /** Credit note confirmed by CFO and written to the supplier credit ledger. */
  CREDIT_RECORDED: "CREDIT_RECORDED",
};

export const CLAIM_INTAKE_KIND = {
  STANDARD: "standard",
  CREDIT_NOTE: "credit_note",
};

export const isCreditNoteClaim = (claim = {}) =>
  String(claim?.intakeKind || "").trim().toLowerCase() ===
  CLAIM_INTAKE_KIND.CREDIT_NOTE;

export const CLAIM_STATUS_ALIASES = {
  PENDING_MANUAL_REVIEW: CLAIM_STATUS.PENDING,
  IN_REVIEW: CLAIM_STATUS.REVIEW,
  "IN REVIEW": CLAIM_STATUS.REVIEW,
  "PARTIAL PAYMENT": CLAIM_STATUS.PARTIAL_PAYMENT,
};

export const normalizeClaimWorkflowStatus = (status) => {
  const normalized = String(status || CLAIM_STATUS.PENDING)
    .trim()
    .toUpperCase();
  if (!normalized) return CLAIM_STATUS.PENDING;
  return CLAIM_STATUS_ALIASES[normalized] || normalized;
};

/** Human-readable labels for status pills */
export const CLAIM_STATUS_LABELS = {
  [CLAIM_STATUS.DRAFT]: "Draft",
  [CLAIM_STATUS.PENDING]: "Pending Review",
  [CLAIM_STATUS.REVIEW]: "In Review",
  [CLAIM_STATUS.PENDING_CFO]: "Pending CFO",
  [CLAIM_STATUS.APPROVED]: "Approved",
  [CLAIM_STATUS.REJECTED]: "Rejected",
  [CLAIM_STATUS.AMENDMENT_REQUESTED]: "Amendment Requested",
  [CLAIM_STATUS.PARTIAL_PAYMENT]: "Partial Payment",
  [CLAIM_STATUS.PAID]: "Paid",
  [CLAIM_STATUS.CREDIT_RECORDED]: "Credit recorded",
  PENDING_MANUAL_REVIEW: "Pending Review",
  IN_REVIEW: "In Review",
  "IN REVIEW": "In Review",
};

export const CLAIM_PENDING_QUEUE_STATUSES = new Set([
  CLAIM_STATUS.DRAFT,
  CLAIM_STATUS.PENDING,
  CLAIM_STATUS.AMENDMENT_REQUESTED,
]);

export const CLAIM_REVIEW_QUEUE_STATUSES = new Set([CLAIM_STATUS.REVIEW]);

export const CLAIM_CFO_QUEUE_STATUSES = new Set([CLAIM_STATUS.PENDING_CFO]);

export const CLAIM_PROCESSED_STATUSES = new Set([
  CLAIM_STATUS.APPROVED,
  CLAIM_STATUS.REJECTED,
  CLAIM_STATUS.PARTIAL_PAYMENT,
  CLAIM_STATUS.PAID,
  CLAIM_STATUS.CREDIT_RECORDED,
  "CANCELLED",
]);

export const CLAIM_PAYMENT_READY_STATUSES = new Set([
  CLAIM_STATUS.APPROVED,
  CLAIM_STATUS.PARTIAL_PAYMENT,
]);

/** CFO may correct a claim that was sent back for amendment, even if they did not create it. */
export const canAmendClaimAsCfo = (claim = {}, access = {}) =>
  normalizeClaimWorkflowStatus(claim?.status) === CLAIM_STATUS.AMENDMENT_REQUESTED &&
  Boolean(access?.canAccessClaimsCfoApproval);

export const isClaimPaymentReady = (claim = {}) => {
  if (isCreditNoteClaim(claim)) return false;
  const status = normalizeClaimWorkflowStatus(claim?.status);
  if (CLAIM_PAYMENT_READY_STATUSES.has(status)) return true;
  if (status === CLAIM_STATUS.PAID) {
    return isClaimEligibleForPayment(claim);
  }
  return false;
};

/** Status for UI when a legacy deposit PAID claim still has an outstanding balance. */
export const resolveClaimDisplayStatus = (claim = {}) => {
  const status = normalizeClaimWorkflowStatus(claim?.status);
  if (status === CLAIM_STATUS.PAID && isClaimEligibleForPayment(claim)) {
    return CLAIM_STATUS.PARTIAL_PAYMENT;
  }
  return status;
};
